const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (json) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// --- Types ---

export interface GRCDocument {
  id: number;
  user_id: number;
  document_name: string;
  original_filename: string;
  category: string;
  grc_pillar: string;
  domain: string;
  jurisdiction_applicability: Array<{ country: string; state?: string }> | null;
  regulations_mapped: string[] | null;
  owner_contact_id: number | null;
  version: number;
  is_current_version: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  upload_date: string;
  last_review_date: string | null;
  next_review_date: string | null;
  expiry_date: string | null;
  status: string;
  download_url: string | null;
}

export interface CategorySummary {
  category: string;
  label: string;
  expected_count: number;
  uploaded_count: number;
  documents: GRCDocument[];
  missing_documents: string[];
}

export interface RepositorySummary {
  total_expected: number;
  total_uploaded: number;
  categories: CategorySummary[];
}

export interface ReadinessCheck {
  ready: boolean;
  missing: string[];
}

export interface DocumentCategories {
  [slug: string]: {
    label: string;
    expected_documents: string[];
  };
}

export interface DocumentUploadMeta {
  document_name: string;
  category: string;
  grc_pillar: string;
  domain: string;
  jurisdiction_applicability?: string;
  regulations_mapped?: string;
  owner_contact_id?: number;
  last_review_date?: string;
  next_review_date?: string;
  expiry_date?: string;
  status?: string;
}

export interface DocumentUpdateMeta {
  document_name?: string;
  category?: string;
  grc_pillar?: string;
  domain?: string;
  jurisdiction_applicability?: Array<{ country: string; state?: string }>;
  regulations_mapped?: string[];
  owner_contact_id?: number | null;
  last_review_date?: string | null;
  next_review_date?: string | null;
  expiry_date?: string | null;
  status?: string;
}

// --- API Functions ---

export async function uploadDocument(file: File, meta: DocumentUploadMeta): Promise<GRCDocument> {
  const token = localStorage.getItem(TOKEN_KEY);
  const fd = new FormData();
  fd.append('file', file);
  fd.append('document_name', meta.document_name);
  fd.append('category', meta.category);
  fd.append('grc_pillar', meta.grc_pillar);
  fd.append('domain', meta.domain);
  if (meta.jurisdiction_applicability) fd.append('jurisdiction_applicability', meta.jurisdiction_applicability);
  if (meta.regulations_mapped) fd.append('regulations_mapped', meta.regulations_mapped);
  if (meta.owner_contact_id) fd.append('owner_contact_id', String(meta.owner_contact_id));
  if (meta.last_review_date) fd.append('last_review_date', meta.last_review_date);
  if (meta.next_review_date) fd.append('next_review_date', meta.next_review_date);
  if (meta.expiry_date) fd.append('expiry_date', meta.expiry_date);
  if (meta.status) fd.append('status', meta.status);

  const res = await fetch(`${API_BASE_URL}/api/v1/documents`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  return parseResponse<GRCDocument>(res);
}

export async function listDocuments(filters?: {
  category?: string;
  grc_pillar?: string;
  domain?: string;
  status?: string;
  jurisdiction?: string;
}): Promise<GRCDocument[]> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_BASE_URL}/api/v1/documents${qs}`, {
    headers: authHeaders(),
  });
  return parseResponse<GRCDocument[]>(res);
}

export async function getDocument(id: number): Promise<GRCDocument> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/${id}`, {
    headers: authHeaders(),
  });
  return parseResponse<GRCDocument>(res);
}

export async function updateDocumentMeta(id: number, data: DocumentUpdateMeta): Promise<GRCDocument> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return parseResponse<GRCDocument>(res);
}

export async function replaceDocument(id: number, file: File, meta: DocumentUploadMeta): Promise<GRCDocument> {
  const token = localStorage.getItem(TOKEN_KEY);
  const fd = new FormData();
  fd.append('file', file);
  fd.append('document_name', meta.document_name);
  fd.append('category', meta.category);
  fd.append('grc_pillar', meta.grc_pillar);
  fd.append('domain', meta.domain);
  if (meta.jurisdiction_applicability) fd.append('jurisdiction_applicability', meta.jurisdiction_applicability);
  if (meta.regulations_mapped) fd.append('regulations_mapped', meta.regulations_mapped);
  if (meta.owner_contact_id) fd.append('owner_contact_id', String(meta.owner_contact_id));
  if (meta.status) fd.append('status', meta.status);

  const res = await fetch(`${API_BASE_URL}/api/v1/documents/${id}/replace`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  return parseResponse<GRCDocument>(res);
}

export async function deleteDocument(id: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`Delete failed (${res.status})`);
  }
}

export async function getRepositorySummary(): Promise<RepositorySummary> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/repository-summary`, {
    headers: authHeaders(),
  });
  return parseResponse<RepositorySummary>(res);
}

export async function getCategories(): Promise<DocumentCategories> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/categories`, {
    headers: authHeaders(),
  });
  return parseResponse<DocumentCategories>(res);
}

export async function getReadiness(): Promise<ReadinessCheck> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/readiness`, {
    headers: authHeaders(),
  });
  return parseResponse<ReadinessCheck>(res);
}

/** Download the stored file through the API — PDFs come back with the
 * corporate frame and a recipient watermark on every page (SRS #37). */
export async function downloadDocument(id: number): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/documents/${id}/download`, { headers: authHeaders(false) });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  return { blob: await res.blob(), filename: m ? m[1] : `document-${id}` };
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
